import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import Button from 'components/Button'
import Select from 'components/Select2'
import AssessmentSelect, { expandSectionsForBackend } from './AssessmentSelect'
import ConfirmationDialog from 'components/ConfirmationDialog'
import SuccessDialog from 'components/SuccessDialog'
import AssignmentProgressModal from '../AssignmentActivity/AssignmentProgressModal'
import ApprovalIcon from 'components/icons/Approval'
import Label from 'components/Form/Label'
import { generateScheduleDates } from 'utils/scheduleUtils'; 
import {
  SectionWrapper,
  TopHeader,
  TopSection,
  ButtonContainer,
  SelectRow,
  ContentSection,
  FormSection,
  SectionTitle,
  EntityTypeOptions,
  EntityTypeOption,
} from '../../Style/style'
import { message, Checkbox, Modal } from 'antd'
import { selectTopicBasedQuestions } from '../../selectors'

const CreateAssessment = ({
  states,
  cities,
  institutes,
  corporateStates,
  corporateCities,
  corporateCompanies,
  fetchStates,
  fetchCities,
  fetchInstitutes,
  fetchAssessments,
  fetchCorporateStates,
  fetchCorporateCities,
  fetchCorporateCompanies,
  createAssessments,
  onClose,
  scheduleAssessments,
  createdAssessments,
  setCreatedAssessments,
  fetchAptitudeTopics,
  roleBasedQuestionsState,

}) => {
  const [currentStep, setCurrentStep] = useState('entity')
  const [entityType, setEntityType] = useState('college') // Default to college
  // Corporate-only: OTP email invite (no portal login) is the DEFAULT.
  // The checkbox below is the opt-out — tick it to send email+password instead.
  const [isOtpInvite, setIsOtpInvite] = useState(true)
  const [selectedEntityID, setSelectedEntityID] = useState(null)
  const [instituteCampusId, setInstituteCampusId] = useState(null)
  const [selectedState, setSelectedState] = useState(null)
  const [selectedCity, setSelectedCity] = useState(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [assessmentData, setAssessmentData] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [isAnyAssessmentEditing, setIsAnyAssessmentEditing] = useState(false)
    const [distributionType, setDistributionType] = useState('one-time');
    const [assessmentForms, setAssessmentForms] = useState({ forms: {} });

  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false)
  const [responseData, setResponseData] = useState(null);
  const [progressJobId, setProgressJobId] = useState(null); // live assignment popup (async)
  const [progressTotal, setProgressTotal] = useState(0);
  const topicQuestionsState = useSelector(selectTopicBasedQuestions);

  
  const userEmail = useSelector(state => state.auth?.userData?.email)

    const formatedEntities = React.useMemo(() => {
    if (entityType === 'college') {
      return (
        institutes?.map(institute => ({
          id: institute.institute_id,
          name: institute.campus_name,
          campusId: institute.institute_campus_id,
        })) || []
      )
    } else {
      return (
        corporateCompanies?.map(company => ({
          id: company.corporate_id,
          name: company.company_name,
        })) || []
      )
    }
  }, [institutes, corporateCompanies, entityType])

  const selectedEntityData = React.useMemo(() => {
    if (!selectedEntityID) return null;

    const selectedEntity = formatedEntities.find(
      entity => entity.id === selectedEntityID
    );

    if (!selectedEntity) return null;

    if (entityType === 'college') {
      // For colleges, we have institute data
      const institute = institutes?.find(inst => inst.institute_id === selectedEntityID);
      return {
        name: institute?.campus_name || selectedEntity.name,
        location: `${selectedCity || 'City'}, ${selectedState || 'State'}`,
        type: 'Institute',
        campusId: institute?.institute_campus_id,
      };
    } else {
      // For corporate, we have company data
      const company = corporateCompanies?.find(comp => comp.corporate_id === selectedEntityID);
      return {
        name: company?.company_name || selectedEntity.name,
        location: selectedCity ? `${selectedCity}, ${selectedState || 'State'}` : selectedState || 'Location',
        type: 'Corporate',
      };
    }
  }, [selectedEntityID, formatedEntities, institutes, corporateCompanies, selectedCity, selectedState, entityType]);



  // Get the appropriate states based on entity type
  const formatedStates = React.useMemo(() => {
    const sourceStates = entityType === 'college' ? states : corporateStates
    return (
      sourceStates?.map(state => ({
        id: state,
        name: state,
      })) || []
    )
  }, [states, corporateStates, entityType])

  // Get the appropriate cities based on entity type
  const formatedCities = React.useMemo(() => {
    const sourceCities = entityType === 'college' ? cities : corporateCities
    return (
      sourceCities?.map(city => ({
        id: city,
        name: city,
      })) || []
    )
  }, [cities, corporateCities, entityType])

  // Format the entities (colleges or companies) based on entity type

  const hasValidSavedAssessments = () => {
    return (
      assessmentData && assessmentData.length > 0 && !isAnyAssessmentEditing
    )
  }

  // Role_Based assessments use Generate Test → Review Panel flow, not Save → Send
  const isAllRoleBased = assessmentData && assessmentData.length > 0 && assessmentData.every(a => a.assessmentType === 'Role_Based');

  // Fetch states on mount based on entity type
  useEffect(() => {
    if (entityType === 'college') {
      fetchStates()
    } else {
      fetchCorporateStates()
      // For corporate, fetch all companies immediately with empty string for city
      fetchCorporateCompanies('')
    }

    // Reset selections when entity type changes
    setSelectedState(null)
    setSelectedCity(null)
    setSelectedEntityID(null)
    setInstituteCampusId(null)
  }, [entityType, fetchStates, fetchCorporateStates, fetchCorporateCompanies])

  // Fetch cities when state changes
  useEffect(() => {
    if (entityType === 'college' && !selectedState) return

    if (entityType === 'corporate' && !selectedState) {
      // For corporate, if state is cleared, fetch all companies
      fetchCorporateCompanies('')
      return
    }

    if (selectedState) {
      if (entityType === 'college') {
        fetchCities(selectedState)
      } else {
        fetchCorporateCities(selectedState)
      }
    }

    if (selectedCity) {
      setSelectedCity(null)
    }

    setSelectedEntityID(null)
    setInstituteCampusId(null)
  }, [
    selectedState,
    entityType,
    fetchCities,
    fetchCorporateCities,
    fetchCorporateCompanies,
  ])

  // Fetch institutes/companies when city changes
  useEffect(() => {
    if (entityType === 'college' && !selectedCity) return

    if (selectedCity) {
      if (entityType === 'college') {
        fetchInstitutes(selectedCity)
      } else {
        fetchCorporateCompanies(selectedCity)
      }
    } else if (entityType === 'corporate') {
      // For corporate, fetch with empty string if no city selected
      fetchCorporateCompanies('')
    }

    setSelectedEntityID(null)
    setInstituteCampusId(null)
  }, [selectedCity, entityType, fetchInstitutes, fetchCorporateCompanies])

  const handleBack = () => {
    if (currentStep === 'assessment') {
      setCurrentStep('entity')
    } else {
      onClose()
    }
  }

  const handleStateChange = value => {
    setSelectedState(value)
    setSelectedCity(null)
    setSelectedEntityID(null)
    setInstituteCampusId(null)
  }

  const handleCityChange = value => {
    setSelectedCity(value)
    setSelectedEntityID(null)
    setInstituteCampusId(null)

    // For corporate, immediately fetch companies when city changes
    if (entityType === 'corporate') {
      fetchCorporateCompanies(value || '')
    }
  }

const handleEntitySelect = (value, option) => {
  setSelectedEntityID(value)
  setInstituteCampusId(option?.option?.campusId)
}
  const handleEntityTypeChange = type => {
    setEntityType(type)
    // OTP invite is the default for corporate; college/institute never use it.
    setIsOtpInvite(type === 'corporate')
  }

  const handleNextClick = () => {
    if (currentStep === 'entity' && selectedEntityID) {
      setCurrentStep('assessment')
    }
  }


  // Format an API error as a user-friendly popup. Quota-exhausted responses
  // from /assessment/assignAssessment include structured fields so the admin
  // knows exactly which type ran out and how many extra seats they tried to add.
  const showSubmissionError = (err) => {
    console.log('[showSubmissionError] received:', err)

    // axios wraps 4xx responses in err.response.data, but some callers may
    // already unwrap the response. Accept both shapes.
    const responseData = err?.response?.data || err?.data || err
    const errorInfo = responseData?.error || err?.error || {}
    const isQuota = errorInfo.code === 'QUOTA_EXHAUSTED'
    const messageText =
      responseData?.message ||
      errorInfo?.message ||
      err?.message ||
      'Failed to process assessments'

    if (isQuota) {
      Modal.error({
        title: 'Assessment Quota Exhausted',
        content: (
          <div style={{ marginTop: 8 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', fontSize: 14 }}>
              <span>Assessment Type:</span><strong>{errorInfo.assessmentType || '—'}</strong>
              <span>Remaining:</span><strong>{errorInfo.remaining ?? '—'}</strong>
              <span>Required:</span><strong>{errorInfo.required ?? '—'}</strong>
            </div>
            <p style={{ marginTop: 12, fontSize: 13, color: '#666' }}>
              Increase the quota or reduce the students.
            </p>
          </div>
        ),
        okText: 'OK',
      })
      return
    }

    Modal.error({
      title: 'Failed to Assign Assessment',
      content: messageText,
      okText: 'OK',
    })
  }

  const handleSendClick = () => {
    setIsDialogOpen(true)
  }

const formatDate = date =>
  date && typeof date.format === 'function'
    ? date.format('YYYY-MM-DD')
    : new Date(date).toISOString().slice(0, 10);

    const handleConfirmSend = async () => {
      try {
        setIsDialogOpen(false);
        
        // Add detailed logging
        console.log('Starting handleConfirmSend with:', {
          assessmentData,
          roleBasedQuestionsState
        });
        
        if (!assessmentData?.length) {
          message.warning('Please save at least one assessment');
          return;
        }
        if (!userEmail) {
          message.error('User email not found. Please log in again.');
          return;
        }
        setIsSubmitting(true);

        const finalEntityId = selectedEntityID;
        if (!finalEntityId) {
          message.error('Entity ID is missing. Please re-select the entity.');
          setIsSubmitting(false);
          return;
        }
    
        // Split assessments by distributionType
        const scheduled = assessmentData.filter(a => a.distributionType === 'scheduled');
        const oneTime = assessmentData.filter(a => a.distributionType !== 'scheduled');
    
        let response = [];
        
        try {
          if (scheduled.length > 0) {
            const schedulePayloads = scheduled.map(form => {
              const allDates = generateScheduleDates(
                form.scheduleStartDate,
                form.scheduleEndDate,
                form.frequencyType,
                form.frequencyValue
              );
              if(allDates.length === 0) {
                message.warning('No valid dates found for the given schedule start and end dates.');
                return;
              }
              console.log("Scheduling assessment:", form);
              return {
                scheduleName: form.name,
                assessmentType: form.assessmentType,
                assessmentConfig: {
                  name: form.name,
                  instructions: form.instructions,
                  allowProctoring: form.allowProctoring,
                  allowVerification: form.allowVerification,
                  ...((form.assessmentType === 'Communication' || form.assessmentType === 'Hinglish') && {
                    cefrLevel: form.cefrLevel,
                    accent: form.accent || 'en-IN',
                    enabledSections: expandSectionsForBackend(form.enabledSections),
                    responseLanguage: form.responseLanguage || 'English',
                    generatedQuestions: form.generatedQuestions || topicQuestionsState?.data || null,
                  }),
                  ...(form.assessmentType === 'Role_Based' && { 
                    roleName: form.roleName,
                    skills: form.skills,
                    seniority: form.seniority,
                    jobDescription: form.jobDescription,
                    industry_domain: form.industry_domain,
                    generatedQuestions: roleBasedQuestionsState?.data?.questions
                  }),
                  ...((form.assessmentType === 'Aptitude') && {
                    aptitudeType: form.aptitudeType,
                    aptitudeSubtopics: form.aptitudeSubtopics,
                    difficultySettings: form.difficultySettings,
                    allowNegativeMarking: form.allowNegativeMarking,
                    duration: form.duration,
                    difficultyLevel: form.difficultyLevel
                  }),
                  ...(form.assessmentType === 'Behavior' && { selectedStreams: form.selectedStreams }),
                  ...(['Cognitive', 'Tech_MCQ', 'Tech_Coding'].includes(form.assessmentType) && { assessmentLink: form.assessmentLink }),
                },
                scheduleStartDate: formatDate(form.scheduleStartDate),
                scheduleEndDate: formatDate(form.scheduleEndDate),
                frequencyType: form.frequencyType,
                frequencyValue: allDates, 
                assessmentValidityDays: form.assessmentValidityDays ?? 1,
                listName: `${form.name} - Student List`,
                studentsData: form.bulkUploadData,
                entityId: finalEntityId,
                entityType: entityType,
                createdBy: userEmail,
              };
            });
    
            console.log("Calling scheduleAssessments with payloads:", schedulePayloads);
            response.push(await scheduleAssessments(schedulePayloads));
          }
          
          if (oneTime.length > 0) {
            // Filter out Role_Based assessments - they use the multi-set generation flow (Generate Test → Review Panel → Assign)
            const nonRoleBased = oneTime.filter(a => a.assessmentType !== 'Role_Based');
            const skippedRoleBased = oneTime.filter(a => a.assessmentType === 'Role_Based');
            if (skippedRoleBased.length > 0) {
              console.log('Skipping Role_Based assessments from Send flow (they use Review Panel):', skippedRoleBased.length);
              if (nonRoleBased.length === 0 && scheduled.length === 0) {
                message.info('Role-Based assessments are managed through the Review Panel. Use "Generate Test" to create sets.');
                setIsSubmitting(false);
                return;
              }
            }
            console.log('Processing one-time assessments:', nonRoleBased);

            const assessmentPayload = nonRoleBased.map(assessment => {
              // CRITICAL FIX: Make a deep copy of the assessment to avoid reference issues
              const basePayload = JSON.parse(JSON.stringify(assessment));
              
              // Add the common fields
              basePayload.entityType = entityType;
              basePayload.entityId = selectedEntityID;
              basePayload.email = userEmail;

              // Corporate-only: opt-in OTP email invite (no portal login).
              // College/institute assignments never send this flag.
              if (entityType === 'corporate') {
                basePayload.isOtpInvite = !!isOtpInvite;
              }
              
              // For Role_Based assessments, make sure we're passing the correct data
              if (assessment.assessmentType === 'Role_Based') {
                console.log('Processing Role_Based assessment:', assessment);
                
                // Log all the fields to see what's available
                console.log('Role-Based assessment fields:', Object.keys(assessment));
                
                // Ensure these specific fields are set correctly
                basePayload.roleName = assessment.roleName;
                
                // CRITICAL FIX: Prioritize selectedSkills over skills
                basePayload.skills = assessment.selectedSkills || assessment.skills || [];
                
                basePayload.seniority = assessment.seniority;
                basePayload.jobDescription = assessment.jobDescription || '';
                basePayload.industry_domain = assessment.industry_domain || '';
                
                // Use the generated questions from the assessment data
                if (assessment.generatedQuestions) {
                  basePayload.generatedQuestions = assessment.generatedQuestions;
                  console.log('Using generatedQuestions from assessment data');
                } else if (roleBasedQuestionsState && roleBasedQuestionsState.data) {
                  // Only use roleBasedQuestionsState as a fallback
                  basePayload.generatedQuestions = roleBasedQuestionsState.data.questions;
                  console.log('Using generatedQuestions from roleBasedQuestionsState');
                }
                
                console.log('Final Role-based assessment payload:', basePayload);
              }

              // For Communication assessments with topic-based generated questions
              if (assessment.assessmentType === 'Communication' || assessment.assessmentType === 'Hinglish') {
                if (assessment.generatedQuestions) {
                  basePayload.generatedQuestions = assessment.generatedQuestions;
                } else if (topicQuestionsState?.data) {
                  basePayload.generatedQuestions = topicQuestionsState.data;
                }
                basePayload.responseLanguage = assessment.responseLanguage || 'English';
                basePayload.accent = assessment.accent || 'en-IN';
              }

              return basePayload;
            });
          
            console.log("Calling createAssessments with payload:", assessmentPayload);
            response.push(await createAssessments(assessmentPayload));
          }
    
          setResponseData(response);

          // If any assignment returned a queue jobId (async path), open the live
          // progress popup instead of the static success dialog.
          const findJobId = (resp) => {
            const outer = Array.isArray(resp) ? resp : [resp];
            for (const inner of outer) {
              const arr = Array.isArray(inner) ? inner : [inner];
              for (const ax of arr) {
                const jid = ax?.data?.data?.jobId;
                if (jid) return { jobId: jid, total: ax?.data?.data?.queued || 0 };
              }
            }
            return null;
          };
          const found = findJobId(response);
          if (found) {
            setProgressJobId(found.jobId);
            setProgressTotal(found.total);
          } else {
            setIsSuccessDialogOpen(true);
          }

          // Check for any scheduled assessments with missing dates
          for (const form of scheduled) {
            if (!form.startDate || !form.endDate) {
              setIsSubmitting(false);
              return;
            }
          }
        } catch (innerErr) {
          console.error('Inner error during submission:', innerErr);
          console.error('Error stack:', innerErr.stack);
          showSubmissionError(innerErr);
        }

      } catch (err) {
        console.error('Error during submission:', err);
        console.error('Error stack:', err.stack);
        showSubmissionError(err);
      } finally {
        setIsSubmitting(false);
      }
    };


  const handleSuccessClose = () => {
    setIsSuccessDialogOpen(false)
    window.location.reload();
  }

  const getEntityLabel = () => {
    return entityType === 'college' ? 'College' : 'Company'
  }

  // Find the selected entity name for the confirmation dialog
  const selectedEntityName = React.useMemo(() => {
    if (!selectedEntityID) return ''

    const selectedEntity = formatedEntities.find(
      entity => entity.id === selectedEntityID
    )
    return selectedEntity?.name || ''
  }, [selectedEntityID, formatedEntities])

  // Determine if entity selection is valid to proceed
  const isEntitySelectionValid = () => {
    if (entityType === 'college') {
      return selectedState && selectedCity && selectedEntityID
    } else {
      // For corporate, we only require the entity ID
      return selectedEntityID
    }
  }

  return (
    <SectionWrapper column>
      <TopSection>
        <TopHeader>Create Assessments</TopHeader>
        <ButtonContainer>
          <Button.Secondary text="Back" onClick={handleBack} />
          {currentStep === 'entity' ? (
            <Button.Primary
              text="Next"
              onClick={handleNextClick}
              disabled={!isEntitySelectionValid()}
            />
          ) : !isAllRoleBased ? (
            <Button.Primary
              text={isSubmitting ? 'Sending...' : 'Send'}
              onClick={handleSendClick}
              disabled={isSubmitting || !hasValidSavedAssessments()}
              style={{
                background: hasValidSavedAssessments() ? '#444CE7' : '#E0E0E0',
                cursor: hasValidSavedAssessments() ? 'pointer' : 'not-allowed',
                opacity: hasValidSavedAssessments() ? 1 : 0.7,
                color: isSubmitting ? '#fff' : undefined,
              }}
            />
          ) : null}
        </ButtonContainer>
      </TopSection>

      <ContentSection>
        {currentStep === 'entity' ? (
          <>
            <SectionTitle>Entity Details</SectionTitle>
            <FormSection>
              <div
                style={{
                  marginBottom: '20px',
                  padding: '10px',
                  backgroundColor: '#f9f9f9',
                  borderRadius: '4px',
                }}
              >
                <Label>Entity Type:</Label>
                <div style={{ marginLeft: '15px', marginTop: '10px' }}>
                  <EntityTypeOptions>
                    <EntityTypeOption
                      isActive={entityType === 'college'}
                      onClick={() => handleEntityTypeChange('college')}
                    >
                      College
                    </EntityTypeOption>
                    <EntityTypeOption
                      isActive={entityType === 'corporate'}
                      onClick={() => handleEntityTypeChange('corporate')}
                    >
                      Corporate
                    </EntityTypeOption>
                  </EntityTypeOptions>
                </div>
              </div>

              <SelectRow>
                <div>
                  <Label>
                    {entityType === 'college' ? 'State *' : 'State'}
                  </Label>
                  <Select
                    placeholder="Select State"
                    showSearch={true}
                    OptionData={formatedStates}
                    onChange={handleStateChange}
                    value={selectedState}
                    valueParam="id"
                    nameParam="name"
                  />
                </div>
                <div>
                  <Label>{entityType === 'college' ? 'City *' : 'City'}</Label>
                  <Select
                    placeholder="Select City"
                    showSearch={true}
                    OptionData={formatedCities}
                    onChange={handleCityChange}
                    value={selectedCity}
                    valueParam="id"
                    nameParam="name"
                    disabled={!selectedState}
                  />
                </div>
              </SelectRow>
              <SelectRow>
                <div>
                  <Label>{getEntityLabel()} *</Label>
                  <Select
                    placeholder={`Select ${getEntityLabel()}`}
                    showSearch={true}
                    OptionData={formatedEntities}
                    onChange={handleEntitySelect}
                    value={selectedEntityID}
                    valueParam="id"
                    nameParam="name"
                    disabled={entityType === 'college' ? !selectedCity : false}
                  />
                </div>
              </SelectRow>
              {entityType === 'corporate' && (
                <div style={{ marginTop: '8px' }}>
                  <Checkbox
                    checked={!isOtpInvite}
                    onChange={e => setIsOtpInvite(!e.target.checked)}
                  >
                    Send email &amp; password instead (portal login, no OTP)
                  </Checkbox>
                  <div
                    style={{
                      marginLeft: '24px',
                      marginTop: '4px',
                      fontSize: '12px',
                      color: '#667085',
                    }}
                  >
                    By default corporate candidates get a no-login OTP invite
                    link. Tick this to create portal accounts and email login
                    credentials instead.
                  </div>
                </div>
              )}
            </FormSection>
          </>
        ) : (
          <AssessmentSelect
            assessmentData={assessmentData}
            setAssessmentData={setAssessmentData}
            selectedEntityID={selectedEntityID}
            fetchAssessments={fetchAssessments}
            setIsEditing={setIsAnyAssessmentEditing}
            createAssessments={createAssessments}
            createdAssessments={createdAssessments}
            setCreatedAssessments={setCreatedAssessments}
            entityType={entityType}
            instituteCampusId={instituteCampusId}
            fetchAptitudeTopics={fetchAptitudeTopics}
            distributionType={distributionType}
            setDistributionType={setDistributionType}
            selectedEntityData={selectedEntityData}
            isOtpInvite={isOtpInvite}
          />
        )}
      </ContentSection>
      <ConfirmationDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onConfirm={handleConfirmSend}
        icon={<ApprovalIcon width="147" height="193" />}
        title={`Send assessments to the ${
          entityType === 'college' ? 'students' : 'candidates'
        } of ${selectedEntityName}?`}
        confirmText="Send"
        cancelText="Keep Editing"
      />
      <SuccessDialog
        isOpen={isSuccessDialogOpen}
        // onClose={handleSuccessClose}
        onClose={handleSuccessClose}
        responseData={responseData}
      />
      <AssignmentProgressModal
        open={!!progressJobId}
        jobId={progressJobId}
        total={progressTotal}
        assessmentName={selectedEntityName}
        onContinue={handleSuccessClose}
        onClose={handleSuccessClose}
        onViewActivity={(jid) => { window.location.href = `/assessment/reviews?view=activity&job=${jid}` }}
      />
    </SectionWrapper>
  )
}

export default CreateAssessment
